<template>
  <b-card class="cardStyle enrollment-inside-card-height">
    <p class="m-0 card-title">
      <b-icon icon="graph-up" scale="1.2"></b-icon>&nbsp; Randomized comparison
    </p>
    <p class="card-subtitle mb-3 mt-1">
      A comparison of the total number of <strong>participants randomized</strong> at your site
      versus the <strong>average total</strong> of the other sites participating in the same study
      in <strong>{{ countryName.trim() }}</strong> and the <strong>average total</strong> of
      <strong>all other sites</strong> in the study
    </p>
    <div v-if="!loadingComparison && !showComparison" style="height: 15rem">
      <div class="text-center custom-margin-y">
        <b-img :src="image" fluid alt="Responsive image"></b-img>
      </div>
      <p class="text-center" style="font-size: 13px">
        Once study data becomes available you will be able to see a comparison of your site and the
        overall study progress
      </p>
    </div>
    <section>
      <comparison
        :comparisonData="comparisonData"
        :countryName="countryName.trim()"
        :height="'24rem'"
        v-if="showComparison"
      ></comparison>
      <b-row class="mt-3" v-if="showComparison">
        <b-col
          xl="4"
          lg="4"
          md="4"
          sm="4"
          cols="12"
          class="text-center comparison-inside-card"
          style="height: 2rem"
        >
          My Site &nbsp;&nbsp;
          <span style="color: #ffc043" class="comparison-card-number">{{ mySite }} </span>
        </b-col>
        <b-col
          xl="4"
          lg="4"
          md="4"
          sm="4"
          cols="12"
          class="text-center comparison-inside-card"
          style="height: 2rem"
        >
          Country Average &nbsp;&nbsp;
          <span style="" class="text-primary comparison-card-number">{{ countryAverage }}</span>
        </b-col>
        <b-col
          xl="4"
          lg="4"
          md="4"
          sm="4"
          cols="12"
          class="text-center comparison-inside-card"
          style="height: 2rem"
        >
          Study Average &nbsp;&nbsp;
          <span style="color: #05a357" class="comparison-card-number">{{ studyAverage }} </span>
        </b-col>
      </b-row>
    </section>

    <div class="text-center text-primary my-5" v-if="loadingComparison">
      <b-spinner class="align-middle" type="grow"></b-spinner>
      <strong>Loading...</strong>
    </div>
  </b-card>
</template>

<script>
  import moment from 'moment';
  import emptyDataImage from '../../../images/empty_data_icon.png';
  import Comparison from '../charts/Comparison';
  import OldComparison from '../charts/OldComparison';
  export default {
    components: { Comparison, OldComparison },
    props: {
      countryName: String,
      studyId: String,
      siteId: String,
      studyFSFV: String,
      studyActualLSLV: String,
      studyPlannedLSLV: String,
      siteFSFV: String,
    },
    data() {
      return {
        image: emptyDataImage,
        comparisonData: [],
        siteComparisonData: [],
        countryComparisonData: [],
        studyComparisonData: [],
        showComparison: false,
        loadingComparison: false,
        countryInitDate: '',
        siteInitDate: '',
        studyInitDate: '',
        mySite: '-',
        countryAverage: '-',
        totalAverage: '-',
        studyAverage: '-',
        totalAverageColor: '#4040D7',
        totalAverageText: '',
        diffFromCountry: null,
        diffFromStudy: null,
        diffFromSite: null,
        countryStartDate: null,
        siteStartDate: null,
        studyStartDate: null,
        endDate: '',
        startDate: '',
      };
    },
    created() {
      this.getEnrollmentOverTime(this.countryName);
    },
    computed: {},
    methods: {
      calcEndDate() {
        if (this.studyActualLSLV) {
          return this.studyActualLSLV;
        }
        if (this.studyPlannedLSLV) {
          return this.studyPlannedLSLV;
        }
        return moment();
      },
      async getEnrollmentOverTime(country) {
        this.loadingComparison = true;
        this.showComparison = false;
        this.comparisonData = [['Enrollments', 'Country', 'Date']];
        this.countryComparisonData = [];
        this.siteComparisonData = [];
        this.studyComparisonData = [];
        country = country.trim();
        let apis = [
          {
            endpoint: '/api/get-enrollment-over-time',
            params: { studyId: this.studyId, country: country, siteId: '' },
          },
          {
            endpoint: '/api/get-enrollment-over-time',
            params: { studyId: this.studyId, country: '', siteId: this.siteId },
          },
          {
            endpoint: '/api/get-enrollment-over-time',
            params: { studyId: this.studyId, country: '', siteId: '' },
          },
          {
            endpoint: `/api/get-site-milestones`,
            params: { study: this.studyId, 'milestone-names': 'Site Activated,Site FSFV' },
          },
        ];
        Promise.allSettled(apis.map(api => axios.get(api['endpoint'], { params: api['params'] })))
          .then(
            await axios.spread((...allData) => {
              let [countryData, siteData, studyData, milestones] = allData;
              this.findChartStartDates(milestones, country);
              let start = [this.studyStartDate, this.siteStartDate, this.countryStartDate].sort(
                (a, b) => new Date(a) - new Date(b),
              );

              this.startDate = start.length > 0 ? start[0] : '';
              this.endDate = this.calcEndDate();
              this.modifyData(countryData, siteData, studyData, country);
              this.loadingComparison = false;
              this.showComparison = !allData.every(
                e => e.value.data.items.length === 0 || e.value.status === 204,
              );
            }),
          )
          .catch(error => {
            console.error(error);
            this.showComparison = false;
            this.loadingComparison = false;
          });
      },
      findChartStartDates(milestones, country) {
        let milestoneArray = ['Site Activated', 'Site FSFV Date', 'Site FSFV'];
        let index = 0;
        while (index < milestoneArray.length) {
          this.studyStartDate = milestones.value.data['items']
            .filter(el => el['actualDate'])
            .filter(el => el['milestoneName'] === milestoneArray[index])
            .map(el => {
              return el['actualDate'];
            })
            .sort((a, b) => new Date(a) - new Date(b))
            .slice(0, 1)
            .shift();
          index++;
          if (this.studyStartDate) {
            break;
          }
        }

        index = 0;
        while (index < milestoneArray.length) {
          this.countryStartDate = milestones.value.data['items']
            .filter(el => el['actualDate'] && el['country'] === country)
            .filter(el => el['milestoneName'] === milestoneArray[index])
            .map(el => {
              return el['actualDate'];
            })
            .sort((a, b) => new Date(a) - new Date(b))
            .slice(0, 1)
            .shift();
          index++;
          if (this.countryStartDate) {
            break;
          }
        }

        index = 0;
        while (index < milestoneArray.length) {
          this.siteStartDate = milestones.value.data['items']
            .filter(el => el['actualDate'] && el['siteNumber'] === this.siteId)
            .filter(el => el['milestoneName'] === milestoneArray[index])
            .map(el => {
              return el['actualDate'];
            })
            .sort((a, b) => new Date(a) - new Date(b))
            .slice(0, 1)
            .shift();
          index++;
          if (this.siteStartDate) {
            break;
          }
        }
      },
      modifyData(countryData, siteData, studyData, country) {
        if (countryData.status === 'fulfilled' && countryData.value.data['items'].length !== 0) {
          this.countryComparisonData = countryData.value.data['items'];
          this.createChartData(countryData.value.data['items'], country);
        }
        if (siteData.status === 'fulfilled' && siteData.value.data['items'].length !== 0) {
          this.siteComparisonData = siteData.value.data['items'];
          this.createChartData(siteData.value.data['items'], 'MySite');
        }
        if (studyData.status === 'fulfilled' && studyData.value.data['items'].length !== 0) {
          this.studyComparisonData = studyData.value.data['items'];
          this.createChartData(studyData.value.data['items'], 'MyStudy');
        }
        this.calculations(
          this.countryComparisonData,
          this.siteComparisonData,
          this.studyComparisonData,
        );
      },
      createChartData(apiData, name) {
        if (name === 'MySite') {
          let previousDays = moment(apiData[0]['actualDate']).diff(moment(this.startDate), 'days');
          if (previousDays >= 0) {
            this.comparisonData.push([0, name, 0]);
            let days = 1;
            for (let i = 0; i < previousDays; i++) {
              this.comparisonData.push([0, name, days]);
              days++;
            }
            for (let i = 0; i < apiData.length; i++) {
              this.comparisonData.push([apiData[i]['enrollments'], name, days]);
              days++;
            }
          } else {
            for (let index = findIndex; index < apiData.length; index++) {
              this.comparisonData.push([
                apiData[index]['enrollments'],
                name,
                moment(apiData[index]['actualDate']).diff(moment(this.startDate), 'days'),
              ]);
            }
          }

          let daysToComplete = moment(this.endDate).diff(
            moment(apiData[apiData.length - 1]['actualDate']),
            'days',
          );
          for (let i = 0; i < daysToComplete; i++) {
            let lastRecord = this.comparisonData[this.comparisonData.length - 1];
            this.comparisonData.push([lastRecord[0], lastRecord[1], lastRecord[2] + 1]);
          }
        } else {
          const countryOrStudy = name === 'MyStudy' ? 'studyStartDate' : 'countryStartDate';
          let previousDays = moment(apiData[0]['actualDate']).diff(moment(this.startDate), 'days');
          if (previousDays >= 0) {
            this.comparisonData.push([0, name, 0]);
            let days = 1;
            for (let i = 0; i < previousDays; i++) {
              this.comparisonData.push([0, name, days]);
              days++;
            }

            for (let i = 0; i < apiData.length; i++) {
              this.comparisonData.push([
                (apiData[i]['enrollments'] / apiData[i]['totalSites']).toFixed(1),
                name,
                days,
              ]);
              days++;
            }
          } else {
            for (let index = findIndex; index < apiData.length; index++) {
              let date = moment(apiData[index]['actualDate']).diff(moment(this.startDate), 'days');
              this.comparisonData.push([
                (apiData[index]['enrollments'] / apiData[index]['totalSites']).toFixed(1),
                name,
                date,
              ]);
            }
          }

          let daysToComplete = moment(this.endDate).diff(
            moment(apiData[apiData.length - 1]['actualDate']),
            'days',
          );
          for (let i = 0; i < daysToComplete; i++) {
            let lastRecord = this.comparisonData[this.comparisonData.length - 1];
            this.comparisonData.push([lastRecord[0], lastRecord[1], lastRecord[2] + 1]);
          }
        }
      },
      totalAverageDecoration() {
        if (this.totalAverage >= 0) {
          this.totalAverageColor = '#4040D7';
          this.totalAverageText = 'Above';
        } else {
          this.totalAverageColor = '#E11900';
          this.totalAverageText = 'Below';
        }
      },
      calculations(countryData, siteData, studyData) {
        if (countryData.length !== 0 && siteData.length !== 0) {
          this.calculateSiteCountry(countryData, siteData);
        }
        if (siteData.length !== 0 && countryData.length === 0) {
          this.calculateSite(siteData);
        }
        if (countryData.length !== 0 && siteData.length === 0) {
          this.calculateCountry(countryData);
        }
        if (countryData.length !== 0 && siteData.length === 0) {
          this.calculateCountry(countryData, studyData);
        }
        if (studyData.length !== 0) {
          this.calculateStudy(studyData, studyData[studyData.length - 1]);
        }
      },
      calculateSiteCountry(countryData, siteData) {
        let countryItem = countryData[countryData.length - 1];
        this.countryAverage = (countryItem['enrollments'] / countryItem['totalSites']).toFixed(1);
        this.mySite = siteData[siteData.length - 1]['enrollments'];
        this.totalAverage = (
          ((this.mySite - this.countryAverage) / this.countryAverage) *
          100
        ).toFixed(1);
        this.totalAverageDecoration(this.totalAverage);
        this.totalAverage = Math.abs(this.totalAverage) + '%';
      },
      calculateSite(siteData) {
        this.countryAverage = '-';
        this.mySite = siteData[siteData.length - 1]['enrollments'];
        this.totalAverage = '-';
      },
      calculateCountry(countryData) {
        let countryItem = countryData[countryData.length - 1];
        this.countryAverage = (countryItem['enrollments'] / countryItem['totalSites']).toFixed(1);
        this.mySite = '-';
        this.totalAverage = '-';
      },
      calculateStudy(studyData, studyItem) {
        if (studyData.length !== 0) {
          this.studyAverage = (studyItem['enrollments'] / studyItem['totalSites']).toFixed(1);
        } else {
          this.studyAverage = '-';
        }
      },
    },
  };
</script>

<style scoped>
  @media (min-width: 992px) {
    .custom-margin-y {
      margin-block: 3rem;
    }
  }
</style>
